import { type ReactNode } from 'react';
import { REDACTED, type Redactable } from '@cumpli/shared';
import { cn } from '../../ui';

/**
 * True when a {@link Redactable} field was masked server-side because the
 * caller's role isn't cleared to see it (the value === the shared REDACTED
 * marker). Accepts `unknown` so it doubles as a type guard on any Redactable<T>.
 */
export function isRedacted(v: unknown): v is typeof REDACTED {
  return v === REDACTED;
}

/**
 * THE single chokepoint for rendering a Redactable field in an answer card.
 * Every non-PUBLIC value in every card passes through here: when it was
 * redacted, it renders an em dash carrying an accessible "no autorizado"
 * explanation (never leaking that a value even exists); otherwise it renders
 * the value (optionally via `render`, e.g. to format a number or map a code).
 */
export function MaybeRedacted<T extends ReactNode>({
  value,
  render,
  className,
}: {
  value: Redactable<T>;
  render?: (v: T) => ReactNode;
  className?: string;
}) {
  if (isRedacted(value)) {
    return (
      <span
        className={cn('text-ink-faint', className)}
        title="No autorizado para tu rol"
        aria-label="Dato no autorizado para tu rol"
      >
        —
      </span>
    );
  }
  return <>{render ? render(value) : value}</>;
}
